import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'

import { Row } from '../Grid'
import { Text } from '../Text'
import { Fragment } from 'react'
import { ConsultationChecked } from '../Icons'

const Radio = ({
  initial,
  checked,
  disabled,
  selected,
  icon,
  name,
  value,
  label,
  register,
  onClick,
  endIcon,
  radioId,
  hiddenInputId,
  variant,
  numberEvents,
  defaultChecked,
  onChange,
  ...props
}) => (
  <Fragment>
    {variant === 'primary' && (
      <Row
        data-testid='radio-id'
        as='label'
        bg='transparent'
        position='relative'
        alignItems='center'
        selected={selected}
        htmlFor={`${name}${value}`}
        onClick={onClick}
        {...props}
      >
        <HiddenInput
          data-testid={hiddenInputId || 'hidden-input-id'}
          disabled={disabled}
          name={name}
          id={`${name}${value}`}
          type='radio'
          value={value}
          ref={register}
          {...(checked && { checked: checked })}
          {...(defaultChecked && { defaultChecked: defaultChecked })}
          {...(onChange && { onChange: onChange })}
        />

        <Row
          mr={10}
          minWidth='16px'
          borderRadius='50%'
          border='0.5px solid'
          borderColor={disabled ? 'gray.n300' : '#CAD0D6'}
          backgroundColor={disabled ? 'snow' : 'transparent'}
          alignItems='center'
          justifyContent='center'
          position='relative'
          size={16}
        >
          <div />
        </Row>

        <Row alignItems='center' {...(endIcon && { flexDirection: 'row-reverse' })}>
          {icon}
          <Text
            mr='8px'
            ml={icon ? '6px' : '0'}
            as='span'
            color={disabled ? 'gray.n300' : 'black'}
            fontSize='16px'
            fontWeight='400'
          >
            {label}
          </Text>
        </Row>
      </Row>
    )}
    {variant === 'secondary' && (
      <Row as='label' position='relative' htmlFor={name} onClick={onClick} {...props}>
        <SquareRadio
          name={name}
          type='radio'
          value={value}
          ref={register}
          disabled={disabled}
          variant={variant}
          checked={checked}
          defaultChecked={defaultChecked}
        />
        <Row
          width='100%'
          py='6px'
          borderRadius='4px'
          justifyContent='center'
          alignItems='center'
          backgroundColor={disabled ? 'snow' : 'paleBlue'}
        >
          <Text
            color={disabled ? 'gray.n300' : 'lightGreen'}
            variant={['tiny', 'small']}
            fontWeight='bold'
          >
            {label}
          </Text>
        </Row>
      </Row>
    )}
    {variant === 'tertiary' && (
      <Row
        data-testid='radio-id'
        as='label'
        bg='transparent'
        position='relative'
        alignItems='center'
        selected={selected}
        htmlFor={name}
        onClick={onClick}
        {...props}
      >
        <HiddenInput
          data-testid={hiddenInputId || 'hidden-input-id'}
          variant={variant}
          disabled={disabled}
          name={name}
          type='radio'
          value={value}
          ref={register}
          checked={checked}
          defaultChecked={defaultChecked}
        />
        <Row
          mr={label ? 10 : 0}
          minWidth='16px'
          borderRadius='50%'
          border='1px solid'
          borderColor={disabled ? 'gray.n300' : checked ? 'transparent' : '#b8b8b8'}
          backgroundColor={disabled ? 'snow' : 'transparent'}
          alignItems='center'
          justifyContent='center'
          position='relative'
          size={23}
        >
          <Row>
            <ConsultationChecked color='white' width='12px' height='12px' />
          </Row>
        </Row>

        <Row alignItems='center' {...(endIcon && { flexDirection: 'row-reverse' })}>
          {icon}
          <Text
            mr='8px'
            ml={icon ? '6px' : '0'}
            as='span'
            color={disabled ? 'gray.n300' : 'black'}
            variant='small'
          >
            {label}
          </Text>
        </Row>
      </Row>
    )}
    {variant === 'quarternary' && (
      <Row
        as='label'
        position='relative'
        data-testid='radio-id'
        htmlFor={name}
        onClick={onClick}
        {...props}
      >
        <SquareRadio
          name={name}
          type='radio'
          value={value}
          ref={register}
          disabled={disabled}
          variant={variant}
          checked={checked}
          defaultChecked={defaultChecked}
        />
        <Row
          width='100%'
          py='6px'
          borderRadius='4px'
          justifyContent='center'
          alignItems='center'
          backgroundColor={disabled ? 'snow' : 'yellow'}
          position={'relative'}
        >
          <Text
            color={disabled ? 'gray.n300' : 'white'}
            variant={['tiny', 'small']}
            fontWeight='400'
          >
            {label}
          </Text>

          <InfoEvents data-testid='InfoEvents-id'>{numberEvents}</InfoEvents>
        </Row>
      </Row>
    )}
  </Fragment>
)

Radio.defaultProps = {
  selected: false,
  disabled: false,
  checked: false,
  endIcon: false,
  variant: 'primary',
  defaultChecked: false
}

Radio.propTypes = {
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  name: PropTypes.string,
  value: PropTypes.number,
  label: PropTypes.string,
  onClick: PropTypes.func,
  endIcon: PropTypes.bool,
  variant: PropTypes.string,
  checked: PropTypes.bool,
  defaultChecked: PropTypes.bool
}

const HiddenInput = styled.input(
  ({ theme: { colors }, disabled, variant }) => `

  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 1;
  z-index: 20;
  cursor: ${disabled ? 'default' : 'pointer'};
  opacity: 0;

   &:not(:checked) {
    left: 0 !important;
    + div {
      div {
        transition: ${variant === 'tertiary' ? 'none' : 'all linear 100ms'};
        opacity: 0;
      }
    }
  }

  &:checked {
    + div {
      div {
        padding: ${variant === 'tertiary' ? 'none' : '3px'};
        border-radius: 50%;
        border: 5px solid ${colors.primary};
        background-color: ${variant === 'tertiary' ? colors.darkPrimary : 'transparent'};
      }
    }
  }
`
)

const SquareRadio = styled.input(
  ({ theme: { colors }, disabled, variant }) => `

  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 1;
  z-index: 20;
  cursor: ${disabled ? 'not-allowed' : 'pointer'};
  opacity: 0;

  &:not(:checked) {
    left: 0 !important;
  }

  &:checked ~ div {
    cursor: pointer;
    transition: all linear 100ms;
    background: ${variant === 'quarternary' ? colors.yellow : colors.lightGreen};
    p {
      color: ${colors.white};
    }
  }

`
)

const InfoEvents = styled(Text)`
  background: #ff7708;
  position: absolute;
  border-radius: 50%;
  height: 22px;
  width: 22px;
  top: -6px;
  right: -6px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ffffff;
  font-size: 12px;
  line-height: normal;
`

export { Radio }
